/**
 * @var string Denotes that when sorting, null and undefined values go last
 */
export const SORT_NULL_UNDEFINED_LAST = 'sortNullUndefinedLast';

/**
 * @var string Denotes that when sorting, null and undefined values go first
 */
export const SORT_NULL_UNDEFINED_FIRST = 'sortNullUndefinedFirst';

/**
 * @var string Denotes that when sorting, null and undefined values by sorted
 * naturally as if it is an empty value
 */
export const SORT_NULL_UNDEFINED_NATURAL = 'sortNullUndefinedNatural';

/**
 * @var string Used to denote sort should be ascending order
 */
export const SORT_ASC = 'asc';

/**
 * @var string Used to denote sort should be descending order
 */
export const SORT_DESC = 'desc';

/**
 * Returns a function that will sort an array of objects based on their value
 * for a given attribute. The attribute can be a property on the object, or
 * a property on a child object based on dot notation.
 * @param {string} property The object property to sort by
 * @param {string} direction The direction to sort by
 * @param {string} defaultDirection The default direction to sort by
 * @param {string} nullUndefinedBehavior How to sort null or undefined values.
 * @return function
 */
export const sortByProperty = function (property, direction, defaultDirection, nullUndefinedBehavior = SORT_NULL_UNDEFINED_LAST) {
    // --- Change attribute into array of properties from dot notation
    const props = property.split('.');
    return (a, b) => {
        // --- Use a while loop to get the value of the property traversing
        // --- through sub-properties if needed
        let i = 0;
        while (i < props.length) {
            a = a[props[i]];
            b = b[props[i]];

            // --- Handle undefined values when traversing by desired behavior
            if (
                (typeof a == 'undefined' && typeof b == 'undefined') ||
                (a === null && b === null)
            ) {
                return 0;
            }
            if (
                (typeof a == 'undefined' && typeof b != 'undefined') ||
                (a === null && b !== null)
            ) {
                if (nullUndefinedBehavior == SORT_NULL_UNDEFINED_LAST) {
                    return 1;
                }
                if (nullUndefinedBehavior == SORT_NULL_UNDEFINED_FIRST) {
                    return -1;
                }
                return (
                    // --- The general idea here (and repeated below) is that if 
                    // --- we aren't forcing null or undefined first or last we'll
                    // --- consider them empty values and sort them based on that
                    // --- assumption whether strings or whatever
                    (direction == SORT_ASC && defaultDirection == SORT_ASC) ||
                    (direction == SORT_DESC && defaultDirection == SORT_DESC)
                ) ? 1 : -1;
            }
            if (
                (typeof a != 'undefined' && typeof b == 'undefined') ||
                (a !== null && b === null)
            ) {
                if (nullUndefinedBehavior == SORT_NULL_UNDEFINED_LAST) {
                    return -1;
                }
                if (nullUndefinedBehavior == SORT_NULL_UNDEFINED_FIRST) {
                    return 1;
                }
                return (
                    (direction == SORT_ASC && defaultDirection == SORT_ASC) ||
                    (direction == SORT_DESC && defaultDirection == SORT_DESC)
                ) ? -1 : 1;
            }
            i++;
        }

        // --- Handle null values if present
        if (a === null && b === null) {
            return 0;
        }
        if (a === null && b !== null) {
            if (nullUndefinedBehavior == SORT_NULL_UNDEFINED_LAST) {
                return 1;
            }
            if (nullUndefinedBehavior == SORT_NULL_UNDEFINED_FIRST) {
                return -1;
            }
            if (!b) {
                // --- For null values (as opposed to undefined properties above)
                // --- if the other value is falsy or empty we'll consider them
                // --- the same and handle them that way.
                return 0;
            }
            return (
                (direction == SORT_ASC && defaultDirection == SORT_ASC) ||
                (direction == SORT_DESC && defaultDirection == SORT_DESC)
            ) ? -1 : 1;
        }
        if (a !== null && b === null) {
            if (nullUndefinedBehavior == SORT_NULL_UNDEFINED_LAST) {
                return -1;
            }
            if (nullUndefinedBehavior == SORT_NULL_UNDEFINED_FIRST) {
                return 1;
            }
            if (!a) {
                return 0
            }
            return (
                (direction == SORT_ASC && defaultDirection == SORT_ASC) ||
                (direction == SORT_DESC && defaultDirection == SORT_DESC)
            ) ? 1 : -1;
        }

        // --- Change null values either to zero
        if (a < b) {
            return direction == SORT_ASC ? -1 : 1;
        } else if (a > b) {
            return direction == SORT_ASC ? 1 : -1;
        } else {
            return 0;
        }
    }
}